/**
 * Compare two semantic version strings
 * @param currentVersion The current app version (e.g., "1.0.0")
 * @param minimumVersion The minimum required version (e.g., "1.2.0")
 * @returns true if currentVersion is less than minimumVersion (upgrade needed), false otherwise
 */
export const isVersionLessThan = (currentVersion: string, minimumVersion: string): boolean => {
  try {
    const parseVersion = (version: string): number[] => {
      return version
        .split(".")
        .map((part) => parseInt(part, 10))
        .filter((num) => !isNaN(num));
    };

    const current = parseVersion(currentVersion);
    const minimum = parseVersion(minimumVersion);

    // Pad arrays to same length with zeros
    const maxLength = Math.max(current.length, minimum.length);
    while (current.length < maxLength) current.push(0);
    while (minimum.length < maxLength) minimum.push(0);

    // Compare version parts
    for (let i = 0; i < maxLength; i++) {
      if (current[i] < minimum[i]) return true; // Upgrade needed
      if (current[i] > minimum[i]) return false; // No upgrade needed
    }

    return false; // Versions are equal, no upgrade needed
  } catch (error) {
    console.error("[Version Comparison] Error comparing versions:", error);
    return false; // On error, don't force upgrade
  }
};

/**
 * Validate if a version string is in valid semantic version format
 * @param version The version string to validate
 * @returns true if valid, false otherwise
 */
export const isValidVersion = (version: string): boolean => {
  if (!version || typeof version !== "string") return false;

  // Basic semantic version pattern (allows 1, 1.0, 1.0.0, etc.)
  const versionPattern = /^\d+(\.\d+)*$/;
  return versionPattern.test(version);
};
